import type { ChartNode, LibraryBlock } from "../build";
import { CARD_SHADOW, ellipse, icon, line, motion, rect, type TemplateNode, text } from "./helpers";

// ---------------------------------------------------------------------------
// Infografiken: timelines, processes with arrows, comparisons, key figures,
// funnel and arrow callouts. Arrows are lines with end caps (scene/line.ts).
// ---------------------------------------------------------------------------

type Line = ReturnType<typeof line>;

/** A line with an arrowhead at its right end. */
const arrow = (
  x: number,
  y: number,
  w: number,
  color: string,
  width: number,
  extra: Partial<Line> = {},
): Line => ({
  ...line(x, y, w, color, width),
  lineEnd: "arrow",
  lineRound: true,
  name: "Pfeil",
  ...extra,
});

const chart = (props: Omit<ChartNode, "kind">): ChartNode => ({ kind: "chart", ...props });

const STEPS = [
  ["2023", "Gründung", "Start mit drei Leuten im Coworking-Space"],
  ["2024", "Erstes Produkt", "Marktstart und die ersten 100 Kunden"],
  ["2025", "Wachstum", "Neues Büro und doppelt so großes Team"],
  ["2026", "Expansion", "Zweiter Standort in Hamburg"],
  ["2027", "Ausblick", "Europaweit verfügbar"],
] as const;

/** Horizontal timeline with `count` steps over 1000 reference px. */
function timeline(count: number): TemplateNode[] {
  const width = 1000;
  const step = width / count;
  return [
    arrow(0, 170, width, "primary", 8, { ...motion("fade") }),
    ...STEPS.slice(0, count).flatMap(([year, title, body], index) => {
      const cx = step * index + step / 2;
      return [
        ellipse({
          x: cx - 26,
          y: 144,
          w: 52,
          h: 52,
          fill: index % 2 === 0 ? "primary" : "accent",
          stroke: { color: "background", width: 8 },
          name: `Punkt ${index + 1}`,
          ...motion("pop", { delay: 200 + index * 250 }),
        }),
        text({
          text: year,
          style: "heading-2",
          size: 44,
          color: "primary",
          x: cx - step / 2 + 8,
          y: 60,
          w: step - 16,
          align: "center",
          ...motion("slide-down", { delay: 200 + index * 250 }),
        }),
        text({
          text: title,
          style: "subline",
          size: count > 4 ? 24 : 30,
          color: "text",
          x: cx - step / 2 + 8,
          y: 230,
          w: step - 16,
          align: "center",
          ...motion("slide-up", { delay: 300 + index * 250 }),
        }),
        text({
          text: body,
          style: "body",
          size: count > 4 ? 22 : 24,
          color: "text-muted",
          x: cx - step / 2 + 8,
          y: count > 3 ? 300 : 280,
          w: step - 16,
          align: "center",
          ...motion("fade", { delay: 400 + index * 250 }),
        }),
      ];
    }),
  ];
}

const TIMELINES: LibraryBlock[] = [3, 4, 5].map((count) => ({
  id: `infografik-zeitstrahl-${count}`,
  title: `Zeitstrahl mit ${count} Schritten`,
  category: "infografik",
  tags: ["Zeitstrahl", "Timeline", "Meilensteine", "Geschichte", "Chronik", "Pfeil"],
  width: 1000,
  height: 400,
  anchor: "center",
  duration: 5000,
  nodes: timeline(count),
}));

/** Three boxes connected by arrows. */
const PROCESS: LibraryBlock = {
  id: "infografik-prozess-pfeile",
  title: "Prozess mit Pfeilen",
  category: "infografik",
  tags: ["Prozess", "Ablauf", "Schritte", "Pfeile", "So geht's", "Workflow"],
  width: 1000,
  height: 300,
  anchor: "center",
  duration: 5000,
  nodes: (
    [
      ["paper-plane-tilt", "Anfragen", "Formular ausfüllen"],
      ["lightbulb", "Beraten", "Wir melden uns in 24 h"],
      ["check-circle", "Loslegen", "Projekt startet"],
    ] as const
  ).flatMap(([name, title, body], index) => [
    rect({
      x: index * 380,
      y: 0,
      w: 240,
      h: 300,
      fill: index === 2 ? "primary" : "surface",
      radius: "lg",
      shadow: CARD_SHADOW,
      name: `Schritt ${index + 1}`,
      ...motion("pop", { delay: index * 400 }),
    }),
    icon({
      icon: name,
      x: index * 380 + 80,
      y: 36,
      w: 80,
      h: 80,
      color: index === 2 ? "on:primary" : "primary",
      ...motion("pop", { delay: 100 + index * 400 }),
    }),
    text({
      text: title,
      style: "subline",
      size: 34,
      color: index === 2 ? "on:primary" : "text",
      x: index * 380 + 16,
      y: 140,
      w: 208,
      align: "center",
      ...motion("fade", { delay: 150 + index * 400 }),
    }),
    text({
      text: body,
      style: "body",
      size: 24,
      color: index === 2 ? "on:primary/85" : "text-muted",
      x: index * 380 + 16,
      y: 196,
      w: 208,
      align: "center",
      ...motion("fade", { delay: 200 + index * 400 }),
    }),
    ...(index < 2
      ? [
          arrow(index * 380 + 262, 150, 100, "accent", 10, {
            ...motion("slide-right", { delay: 300 + index * 400 }),
          }),
        ]
      : []),
  ]),
};

/** Two columns: before/after or pro/contra. */
function comparison(
  id: string,
  title: string,
  tags: string[],
  left: { title: string; icon: "x-circle" | "warning"; items: string },
  right: { title: string; icon: "check-circle" | "star"; items: string },
): LibraryBlock {
  return {
    id,
    title,
    category: "infografik",
    tags,
    width: 1000,
    height: 520,
    anchor: "center",
    duration: 5000,
    nodes: [
      rect({
        x: 0,
        y: 0,
        w: 440,
        h: 520,
        fill: "surface",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: left.title,
        ...motion("slide-right"),
      }),
      rect({
        x: 560,
        y: 0,
        w: 440,
        h: 520,
        fill: "primary",
        radius: "lg",
        shadow: CARD_SHADOW,
        name: right.title,
        ...motion("slide-left"),
      }),
      arrow(455, 260, 90, "accent", 12, { ...motion("pop", { delay: 500 }) }),
      icon({ icon: left.icon, x: 40, y: 40, w: 70, h: 70, color: "text-muted" }),
      icon({ icon: right.icon, x: 600, y: 40, w: 70, h: 70, color: "on:primary" }),
      text({
        text: left.title,
        style: "heading-2",
        size: 48,
        color: "text",
        x: 130,
        y: 45,
        w: 290,
      }),
      text({
        text: right.title,
        style: "heading-2",
        size: 48,
        color: "on:primary",
        x: 690,
        y: 45,
        w: 290,
      }),
      text({
        text: left.items,
        style: "body",
        size: 32,
        color: "text",
        x: 40,
        y: 150,
        w: 370,
        ...motion("fade", { delay: 300 }),
      }),
      text({
        text: right.items,
        style: "body",
        size: 32,
        color: "on:primary",
        x: 600,
        y: 150,
        w: 370,
        ...motion("fade", { delay: 600 }),
      }),
    ],
  };
}

const COMPARISONS: LibraryBlock[] = [
  comparison(
    "infografik-vorher-nachher",
    "Vergleich Vorher/Nachher",
    ["Vergleich", "Vorher", "Nachher", "Verbesserung", "Pfeil"],
    {
      title: "Vorher",
      icon: "x-circle",
      items: "• Lange Wartezeiten\n• Viel Papierkram\n• Kein Überblick",
    },
    {
      title: "Nachher",
      icon: "check-circle",
      items: "• Antwort am selben Tag\n• Alles digital\n• Status jederzeit",
    },
  ),
  comparison(
    "infografik-pro-contra",
    "Pro und Contra",
    ["Pro", "Contra", "Vorteile", "Nachteile", "Abwägung", "Vergleich"],
    { title: "Contra", icon: "warning", items: "• Höhere Anschaffung\n• Einarbeitung nötig" },
    {
      title: "Pro",
      icon: "star",
      items: "• Spart jede Woche Zeit\n• Weniger Fehler\n• Zufriedenere Kunden",
    },
  ),
];

/** Three key figures with icons in a row. */
const FIGURES: LibraryBlock = {
  id: "infografik-kennzahlen-reihe",
  title: "Kennzahlen-Reihe",
  category: "infografik",
  tags: ["Kennzahlen", "KPI", "Zahlen", "Fakten", "Statistik", "Icons"],
  width: 1000,
  height: 300,
  anchor: "center",
  duration: 5000,
  nodes: (
    [
      ["trend-up", "+38 %", "mehr Umsatz"],
      ["heart", "4,9", "Kundenbewertung"],
      ["globe", "12", "Länder"],
    ] as const
  ).flatMap(([name, value, label], index) => [
    ellipse({
      x: index * 350 + 95,
      y: 0,
      w: 110,
      h: 110,
      fill: "primary",
      name: `Kreis ${index + 1}`,
      ...motion("pop", { delay: index * 300 }),
    }),
    icon({
      icon: name,
      x: index * 350 + 122,
      y: 27,
      w: 56,
      h: 56,
      color: "on:primary",
      ...motion("pop", { delay: 100 + index * 300 }),
    }),
    text({
      text: value,
      style: "heading-1",
      size: 76,
      color: "primary",
      x: index * 350,
      y: 125,
      w: 300,
      align: "center",
      ...motion("slide-up", { delay: 150 + index * 300 }),
    }),
    text({
      text: label,
      style: "subline",
      size: 28,
      color: "text-muted",
      x: index * 350,
      y: 230,
      w: 300,
      align: "center",
      ...motion("fade", { delay: 250 + index * 300 }),
    }),
  ]),
};

/** Funnel: stages getting narrower, with numbers. */
const FUNNEL: LibraryBlock = {
  id: "infografik-trichter",
  title: "Trichter (Funnel)",
  category: "infografik",
  tags: ["Trichter", "Funnel", "Conversion", "Vertrieb", "Marketing", "Stufen"],
  width: 800,
  height: 560,
  anchor: "center",
  duration: 5000,
  nodes: (
    [
      ["Besucher", "12.400"],
      ["Interessenten", "3.100"],
      ["Angebote", "840"],
      ["Kunden", "210"],
    ] as const
  ).flatMap(([label, value], index) => {
    const w = 800 - index * 140;
    const fills = ["primary", "primary/80", "accent", "accent/80"];
    return [
      rect({
        x: (800 - w) / 2,
        y: index * 140,
        w,
        h: 120,
        fill: fills[index] as string,
        radius: "md",
        name: label,
        ...motion("slide-down", { delay: index * 250 }),
      }),
      text({
        text: `${value}  ${label}`,
        style: "subline",
        size: 36,
        color: index < 2 ? "on:primary" : "on:accent",
        x: (800 - w) / 2 + 20,
        y: index * 140,
        w: w - 40,
        h: 120,
        align: "center",
        ...motion("fade", { delay: 150 + index * 250 }),
      }),
    ];
  }),
};

/** Callouts: a label with an arrow pointing at something on the page. */
const CALLOUTS: LibraryBlock[] = [
  {
    id: "infografik-pfeil-callout-rechts",
    title: "Pfeil-Hinweis nach rechts",
    category: "infografik",
    tags: ["Pfeil", "Hinweis", "Callout", "Beschriftung", "Anmerkung", "Zeiger"],
    width: 700,
    height: 200,
    anchor: "center",
    duration: 4000,
    nodes: [
      rect({
        x: 0,
        y: 40,
        w: 380,
        h: 120,
        fill: "accent",
        radius: "full",
        shadow: CARD_SHADOW,
        name: "Hinweis",
        ...motion("pop"),
      }),
      text({
        text: "Neu im Sortiment!",
        style: "subline",
        size: 36,
        color: "on:accent",
        x: 20,
        y: 40,
        w: 340,
        h: 120,
        align: "center",
        ...motion("fade", { delay: 150 }),
      }),
      arrow(400, 100, 290, "accent", 10, { ...motion("slide-right", { delay: 300 }) }),
    ],
  },
  {
    id: "infografik-pfeil-callout-schraeg",
    title: "Pfeil-Hinweis schräg",
    category: "infografik",
    tags: ["Pfeil", "Hinweis", "Callout", "Beschriftung", "schräg", "Zeiger"],
    width: 600,
    height: 420,
    anchor: "center",
    duration: 4000,
    nodes: [
      text({
        text: "Hier klicken",
        style: "heading-2",
        size: 60,
        color: "primary",
        x: 0,
        y: 0,
        w: 520,
        ...motion("slide-down"),
      }),
      arrow(60, 260, 380, "primary", 10, {
        rotation: 35,
        lineStart: "circle",
        ...motion("fade", { delay: 300 }),
      }),
    ],
  },
];

/** Mini bar chart with a highlighted figure. */
const CHART_FACT: LibraryBlock = {
  id: "infografik-diagramm-fakt",
  title: "Kennzahl mit Mini-Diagramm",
  category: "infografik",
  tags: ["Diagramm", "Kennzahl", "Wachstum", "Säulen", "Statistik", "Fakt"],
  width: 900,
  height: 380,
  anchor: "center",
  duration: 5000,
  nodes: [
    rect({
      x: 0,
      y: 0,
      w: 900,
      h: 380,
      fill: "background",
      radius: "lg",
      shadow: CARD_SHADOW,
      name: "Karte",
      ...motion("fade"),
    }),
    text({
      text: "+62 %",
      style: "heading-1",
      size: 110,
      color: "primary",
      x: 50,
      y: 70,
      w: 380,
      ...motion("pop", { delay: 200 }),
    }),
    text({
      text: "mehr Bestellungen als im Vorjahr",
      style: "body",
      size: 32,
      color: "text-muted",
      x: 50,
      y: 210,
      w: 360,
      ...motion("fade", { delay: 400 }),
    }),
    chart({
      chart: "column",
      x: 460,
      y: 50,
      w: 400,
      h: 280,
      labels: ["2023", "2024", "2025", "2026"],
      series: [{ name: "Bestellungen", values: [320, 410, 480, 780] }],
      axis: true,
      gridlines: false,
      fontSize: 24,
      name: "Diagramm",
      ...motion("slide-up", { delay: 300 }),
    }),
  ],
};

export const INFOGRAPHICS: LibraryBlock[] = [
  ...TIMELINES,
  PROCESS,
  ...COMPARISONS,
  FIGURES,
  FUNNEL,
  ...CALLOUTS,
  CHART_FACT,
];
